import {
  IconBox,
  IconBuildingStore,
  IconChartArrowsVertical,
  IconChartBar,
  IconCreditCard,
  IconReceipt,
} from '@tabler/icons-react'
import { useEffect, useMemo, useState } from 'react'

import {
  Leaderboard,
  MasterStackBarChart,
  ScalrCarousel,
  TreeTable,
} from '@/components/customComponents'
import { createTab, TabConfig } from '@/components/TabSkeleton'
import { DateRangeValidator } from '@/components/ui/charts/DateRangeValidator'
import { MasterChart } from '@/components/ui/charts/MasterChart'
import { Tabs, TabsList } from '@/components/ui/tabs'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

import { chartColors } from '@/constants/COLOR_PALETTE'
import { toast, usePnLMetrics, usePnLStackChartMetrics } from '@/hooks'
import usePnlStoreHealthCheck from '@/hooks/clarity/usePnlStoreHealthCheck'
import {
  selectPageRefreshTrigger,
  useAppSelector,
  useReduxBaseFilter,
} from '@/store'
import { dateToString } from '@/utils'

const MINI_METRIC_CONFIGS = [
  {
    key: 'storeProfits' as const,
    header: 'Store Profits',
    icon: (
      <IconCreditCard className="w-6 h-6" style={{ color: chartColors[0] }} />
    ),
  },
  {
    key: 'netSales' as const,
    header: 'Net Sales',
    icon: (
      <IconChartArrowsVertical
        className="w-6 h-6"
        style={{ color: chartColors[1] }}
      />
    ),
  },
  {
    key: 'cogs' as const,
    header: 'Cogs',
    icon: <IconBox className="w-6 h-6" style={{ color: chartColors[2] }} />,
  },
  {
    key: 'storeLabor' as const,
    header: 'Store Labor',
    icon: (
      <IconChartBar className="w-6 h-6" style={{ color: chartColors[3] }} />
    ),
  },
  {
    key: 'opEx' as const,
    header: 'OpEx',
    icon: <IconReceipt className="w-6 h-6" style={{ color: chartColors[4] }} />,
  },
  {
    key: 'rentAndOccupancy' as const,
    header: 'Rent and Occupancy',
    icon: (
      <IconBuildingStore
        className="w-6 h-6"
        style={{ color: chartColors[5] }}
      />
    ),
  },
]

const CHART_DATA_CONFIGS = [
  {
    key: 'cogs' as const,
    header: 'Cogs',
    icon: <IconBox className="w-6 h-6" style={{ color: chartColors[2] }} />,
  },
  {
    key: 'storeLabor' as const,
    header: 'Store Labor',
    icon: (
      <IconChartBar className="w-6 h-6" style={{ color: chartColors[3] }} />
    ),
  },
  {
    key: 'opEx' as const,
    header: 'OpEx',
    icon: <IconReceipt className="w-6 h-6" style={{ color: chartColors[4] }} />,
  },
  {
    key: 'rentAndOccupancy' as const,
    header: 'Rent and Occupancy',
    icon: (
      <IconBuildingStore
        className="w-6 h-6"
        style={{ color: chartColors[5] }}
      />
    ),
  },
]

export default function ProfitAndLossPage() {
  const { selectedBranch, date, dateFilterType } = useReduxBaseFilter()

  const refreshTrigger = useAppSelector(
    selectPageRefreshTrigger('profit-and-loss')
  )

  const [executeStackChartMetrics, setExecuteStackChartMetrics] =
    useState(false)

  useEffect(() => {
    const timer = setTimeout(() => {
      setExecuteStackChartMetrics(true)
    }, 1500)

    return () => clearTimeout(timer)
  }, [])

  const {
    healthData,
    storeHealthCheckColumns,
    loading: storeHealthCheckLoading,
    error: storeHealthCheckError,
    refetch: storeHealthCheckRefetch,
  } = usePnlStoreHealthCheck(
    dateToString(date, 'from', 'yyyy-mm'),
    dateToString(date, 'to', 'yyyy-mm'),
    dateFilterType
  )

  const {
    hierarchyData,
    metricCardData,
    metricCardTrendData,
    pnlTableColumns,
    loading,
    error,
    refetch: pnlRefetch,
  } = usePnLMetrics(
    dateToString(date, 'from', 'yyyy-mm'),
    dateToString(date, 'to', 'yyyy-mm'),
    selectedBranch
  )

  const {
    metricCardRecord,
    loading: stackChartLoading,
    error: stackChartError,
    refetch: stackChartRefetch,
  } = usePnLStackChartMetrics(
    executeStackChartMetrics ? selectedBranch : '',
    executeStackChartMetrics ? date : undefined
  )

  const getHierarchyDataByTopLevel = (id: string) => {
    return hierarchyData?.find((item) => item.id === id)?.children || []
  }

  const { tabs } = useMemo(() => {
    const configs: TabConfig[] = [
      {
        value: 'sales',
        title: 'Sales',
        content: (
          <PnLTable
            tableData={getHierarchyDataByTopLevel('super_sales')}
            pnlTableColumns={pnlTableColumns}
            loading={loading}
          />
        ),
      },
      {
        value: 'cogs',
        title: 'COGS',
        content: (
          <PnLTable
            tableData={getHierarchyDataByTopLevel('super_cogs')}
            pnlTableColumns={pnlTableColumns}
            loading={loading}
          />
        ),
      },
      {
        value: 'storeLabor',
        title: 'Store Labor',
        content: (
          <PnLTable
            tableData={getHierarchyDataByTopLevel('super_labor')}
            pnlTableColumns={pnlTableColumns}
            loading={loading}
          />
        ),
      },
      {
        value: 'opEx',
        title: 'OpEx',
        content: (
          <PnLTable
            tableData={getHierarchyDataByTopLevel('super_opex')}
            pnlTableColumns={pnlTableColumns}
            loading={loading}
          />
        ),
      },
      {
        value: 'rentAndOccupancy',
        title: 'Rent & Occupancy',
        content: (
          <PnLTable
            tableData={getHierarchyDataByTopLevel('super_rent_&_occupancy')}
            pnlTableColumns={pnlTableColumns}
            loading={loading}
          />
        ),
      },
      {
        value: 'aboveUnitAG',
        title: 'Store EBITDA',
        content: (
          <PnLTable
            tableData={getHierarchyDataByTopLevel('super_above_unit')}
            pnlTableColumns={pnlTableColumns}
            loading={loading}
          />
        ),
      },
    ]

    return {
      tabs: configs.map((config) => createTab(config)),
    }
  }, [hierarchyData, pnlTableColumns, loading])

  useEffect(() => {
    if (refreshTrigger) {
      console.log('Refreshing profit and loss page data...')
      pnlRefetch()
      storeHealthCheckRefetch()
      stackChartRefetch()
    }
  }, [refreshTrigger, pnlRefetch, storeHealthCheckRefetch, stackChartRefetch])

  useEffect(() => {
    if (error || storeHealthCheckError || stackChartError) {
      toast({
        title: 'Something went wrong.',
        description:
          error?.message ||
          storeHealthCheckError?.message ||
          stackChartError?.message,
        variant: 'destructive',
      })
    }
  }, [error])

  const getTruncatedBranchNames = (branchNames: string) => {
    const branches = branchNames.split(',').map((name) => name.trim())
    const fullText = branches.join(', ')

    if (branches.length <= 2) {
      return { displayText: fullText, fullText, shouldTruncate: false }
    }

    const firstBranch = branches[0]
    const secondBranch = branches[1]

    const displayText = `${firstBranch}, ${secondBranch}${branches.length > 2 ? ', ...' : ''}`

    return { displayText, fullText, shouldTruncate: true }
  }

  return (
    <div className="space-y-6">
      <div className="space-y-3 mb-10">
        <h2 className="text-2xl tracking-tight">LeaderBoard</h2>
        <ScalrCarousel>
          <Leaderboard
            data={healthData?.netSales}
            title="Net Sales"
            columns={storeHealthCheckColumns}
            loading={storeHealthCheckLoading}
            sortBy="percentageTrend"
            enablePagination
          />
          <Leaderboard
            data={healthData?.cogs}
            title="Cogs"
            columns={storeHealthCheckColumns}
            loading={storeHealthCheckLoading}
            sortBy="percentageTrend"
            sortOrder="asc"
            enablePagination
          />
          <Leaderboard
            data={healthData?.storeLabor}
            title="Store Labor"
            columns={storeHealthCheckColumns}
            loading={storeHealthCheckLoading}
            sortBy="percentageTrend"
            sortOrder="asc"
            enablePagination
          />
          <Leaderboard
            data={healthData?.opEx}
            title="OpEx"
            columns={storeHealthCheckColumns}
            loading={storeHealthCheckLoading}
            sortBy="percentageTrend"
            sortOrder="asc"
            enablePagination
          />
          <Leaderboard
            data={healthData?.ebitda}
            title="EBITDA"
            columns={storeHealthCheckColumns}
            loading={storeHealthCheckLoading}
            sortBy="percentageTrend"
            sortOrder="asc"
            enablePagination
          />
        </ScalrCarousel>
      </div>

      <MasterStackBarChart
        title="Breakdown"
        metricCardRecord={metricCardRecord}
        miniCardData={metricCardData}
        trendData={metricCardTrendData}
        loading={stackChartLoading}
        miniMetricConfigs={MINI_METRIC_CONFIGS}
        chartDataConfigs={CHART_DATA_CONFIGS}
      />

      <div>
        <div className="flex items-center gap-2 mb-4">
          {(() => {
            const { displayText, fullText, shouldTruncate } =
              getTruncatedBranchNames(selectedBranch)

            if (!shouldTruncate) {
              return <h3 className="text-2xl tracking-tight">{displayText}</h3>
            }

            return (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <h3 className="text-2xl tracking-tight cursor-help">
                      {displayText}
                    </h3>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">{fullText}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )
          })()}
        </div>
        <Tabs defaultValue="sales" className="w-full">
          <TabsList className="w-full">
            {tabs.map((tab) => tab.trigger)}
          </TabsList>
          {tabs.map((tab) => tab.content)}
        </Tabs>
      </div>

      <DateRangeValidator
        dateRange={date}
        chartType="profit & loss chart"
        chartTitle="Profit & Loss Chart"
      >
        <MasterChart
          chartType="pnl"
          dateRange={date}
          selectedBranchFilter={selectedBranch}
          chartTitle="Profit & Loss Chart"
          isBeta
        />
      </DateRangeValidator>
    </div>
  )
}

function PnLTable({
  tableData,
  pnlTableColumns,
  loading,
}: {
  tableData: any[]
  pnlTableColumns: any[]
  loading: boolean
}) {
  return (
    <TreeTable
      data={tableData}
      columns={pnlTableColumns}
      isCard={false}
      loading={loading}
      fitContent={true}
    />
  )
}
